import { useMemo, useState } from 'react';
import { Download, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useDeletePiece,
  useDownloadPiece,
  usePieces,
} from '../hooks/use-dossier-company';
import { PieceUploadDialog } from './piece-upload-dialog';
import { PieceStatusBadge } from './piece-status-badge';
import { PIECE_TYPES_ORDER, PIECE_TYPE_LABELS } from './piece-types';
import type { PermanentFile, PieceType, ValidityStatus } from '../types/dossier-company.types';

const STATUS_PRIORITY: Record<ValidityStatus, number> = {
  expiree: 0,
  expire_bientot: 1,
  valide: 2,
  indetermine: 3,
};

export function PiecesTab() {
  const [typeFilter, setTypeFilter] = useState<PieceType | 'ALL'>('ALL');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const { data, isLoading } = usePieces(
    typeFilter !== 'ALL' ? { type: typeFilter } : undefined,
  );
  const deletePiece = useDeletePiece();
  const download = useDownloadPiece();

  const sorted = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      const priA = STATUS_PRIORITY[a.status];
      const priB = STATUS_PRIORITY[b.status];
      if (priA !== priB) return priA - priB;
      const expA = computeExpiry(a);
      const expB = computeExpiry(b);
      if (!expA && !expB) return 0;
      if (!expA) return 1;
      if (!expB) return -1;
      return expA.getTime() - expB.getTime();
    });
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as PieceType | 'ALL')}>
            <SelectTrigger className="w-[260px]">
              <SelectValue placeholder="Tous les types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous les types</SelectItem>
              {PIECE_TYPES_ORDER.map((t) => (
                <SelectItem key={t} value={t}>
                  {PIECE_TYPE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setUploadOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Ajouter une pièce
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Délivrée</TableHead>
              <TableHead>Expire</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <>
                {[0, 1, 2].map((i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!isLoading && sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Aucune pièce. Cliquez sur « Ajouter une pièce » pour commencer.
                </TableCell>
              </TableRow>
            )}
            {sorted.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.filename}</TableCell>
                <TableCell>{PIECE_TYPE_LABELS[p.type]}</TableCell>
                <TableCell>{formatDate(p.deliveredAt)}</TableCell>
                <TableCell>{formatDate(computeExpiry(p)?.toISOString() ?? null)}</TableCell>
                <TableCell>
                  <PieceStatusBadge status={p.status} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => download.mutate(p.id)}
                      title="Télécharger"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setConfirmDeleteId(p.id)}
                      title="Archiver"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PieceUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />

      <AlertDialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver cette pièce ?</AlertDialogTitle>
            <AlertDialogDescription>
              La pièce sera marquée comme supprimée et purgée du stockage après 30 jours.
              L'action peut être annulée en restaurant la pièce avant la purge.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDeleteId !== null) {
                  deletePiece.mutate(confirmDeleteId);
                  setConfirmDeleteId(null);
                }
              }}
            >
              Archiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}

function computeExpiry(p: PermanentFile): Date | null {
  if (!p.deliveredAt || !p.validityMonths) return null;
  const d = new Date(p.deliveredAt);
  d.setMonth(d.getMonth() + p.validityMonths);
  return d;
}
